import { useRef, useEffect, useContext, useState, useCallback } from "react";
import MainStateContext from "../../../state/mainStateContext";
import { StateContextType } from "../../../types/state.types";
import { setAddressAutoCompleteUsed, wvusLogger } from "../../../helpers";
import { guessAddressUsingGeocoder, interceptEnterKeySelection } from "./AddressAutoComplete.utils";
import { getPhrase, Say } from "../../../languages";

type AddressAutoCompleteProps = {
  formMethods: { validateForm: () => void };
  disabled?: boolean;
  readOnly?: boolean;
  visible: boolean;
  setToggleFlag: (toggle: string) => void;
};

const AddressAutoComplete = (props: AddressAutoCompleteProps) => {
  const { disabled = false, visible, readOnly = false, formMethods, setToggleFlag } = props;
  const { dispatch } = useContext(MainStateContext) as StateContextType;
  const autoCompleteRef = useRef(null);
  const inputFieldRef = useRef(null);
  const displayStyle = visible ? {} : { display: "none" };
  const [wasAutoFilled, setWasAutoFilled] = useState(false);
  const [previousValue, setPreviousValue] = useState("");

  const handlePlaceChange = useCallback(async () => {
    const place = await autoCompleteRef.current.getPlace();
    setToggleFlag("on");

    // For analytics, set a session flag indicating that the address auto-complete feature has been used.
    setAddressAutoCompleteUsed();

    if (place) {
      dispatch({ type: "SET_ADDRESS", payload: place });
    }
  }, [dispatch, setToggleFlag]);

  const fieldPlaceholder = `${getPhrase("payment_step.field_labels.address_single_line")} *`;

  // On first render of the component ...:
  useEffect(() => {
    if (!window?.google?.maps?.places?.Autocomplete) {
      // If the Google Maps API is unavailable, fail gracefully by signaling the parent component
      // to "toggle" (expand all address fields and hide this field).
      const errorMessage = "Unable to load google places Autocomplete.";
      wvusLogger.log(errorMessage, wvusLogger.getLogLevelNames().ERROR);
      console.error(errorMessage);
      setToggleFlag("on");
    } else {
      // Otherwise, attach a new Google Places Autocomplete instance to the input field.
      const fieldOptions = {
        componentRestrictions: { country: "US" },
        fields: ["address_components", "formatted_address"],
        types: ["address"]
      };

      if (inputFieldRef.current) {
        autoCompleteRef.current = new window.google.maps.places.Autocomplete(
          inputFieldRef.current,
          fieldOptions
        );
      }

      autoCompleteRef.current?.addListener("place_changed", handlePlaceChange);

      // Cleanup function to prevent memory leaks.
      return () => {
        if (autoCompleteRef.current) {
          window.google.maps.event.clearInstanceListeners(autoCompleteRef.current);
        }
      };
    }
  }, [handlePlaceChange, setToggleFlag]);

  // This key down event handler intercepts the "Enter" key and translate it into "ArrowDown"
  // and "Enter" key events that simulate selecting an address.
  const onKeyDown = interceptEnterKeySelection;

  const onInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const characterChangeThreshold = 7;

    if (e.target.value.length - previousValue.length < characterChangeThreshold) {
      // A small amount of input is likely manual.
      setWasAutoFilled(false);
    } else {
      // A large amount of input is likely browser auto-filled.
      setWasAutoFilled(true);
    }
    setPreviousValue(e.target.value);
  };

  // If the user pastes text into the input field, we want to treat it the same as if they typed.
  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    // Pasted text is unlikely to be autofill.
    setWasAutoFilled(false);

    // Proactively set the previous value to the pasted value so that the onInput event handler
    // doesn't contradict this auto-fill decision.
    setPreviousValue(e.clipboardData.getData("text"));
  };

  // When the user leaves the field, we attempt to guess the address using the Google Geocoder API,
  // but not if they used browser auto-fill, which will only include the first line of the address,
  // leading to ambiguity and bad guesses from the geocoder.
  const onBlur = useCallback(async () => {
    // Defer to browser auto-fill.
    if (!wasAutoFilled) {
      // If the field has an address that can be confidently guessed, we use it.
      const geocoderResult: google.maps.GeocoderResult = await guessAddressUsingGeocoder(
        inputFieldRef?.current?.value
      );

      if (geocoderResult) {
        dispatch({ type: "SET_ADDRESS", payload: geocoderResult });
      }
    }

    // In all cases where the user has entered something in the field, we want to expand the fields
    // and validate (regardless of whether we have guessed an address).
    setToggleFlag("on");
    formMethods.validateForm();
  }, [wasAutoFilled, setToggleFlag, dispatch]);

  return (
    <div
      className={`wvus-address-autocomplete address-section__autocomplete form-group has-icon`}
      data-field-container={"searchaddress"}
      data-field-is-valid={true}
      style={displayStyle}>
      <label htmlFor={"searchaddress"} className={"is-required"}>
        <Say phrase="payment_step.field_labels.address_single_line" /> <sup>{"*"}</sup>
      </label>
      <div className={`form-control-wrapper`}>
        <input
          ref={inputFieldRef}
          id="searchaddress"
          name="searchaddress"
          className={`form-control`}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          onInput={onInput}
          onPaste={onPaste}
          type={"search"}
          data-phrase-key={"payment_step.field_labels.address_single_line"}
          placeholder={fieldPlaceholder}
          readOnly={readOnly}
          disabled={disabled}
          autoCapitalize="off"
          autoComplete="off"
          autoCorrect="off"
          data-lpignore="true"
          spellCheck="false"
        />
      </div>
    </div>
  );
};

export default AddressAutoComplete;
